// Thermal die-cut label printing (DYMO LabelWriter, Brother QL, Zebra, Rollo, …).
//
// WHY THIS EXISTS: these printers feed die-cut roll labels SHORT-EDGE-FIRST, so the
// driver media is PORTRAIT (short side across the print head, long side = feed).
// The editor lays an address label out LANDSCAPE (89×28). Printing a landscape page
// onto portrait die-cut media makes the gap sensor mis-register — the label comes out
// blank or shifted (the reported "#30252 doesn't print / doesn't align"). The fix is
// to rotate the rendered bitmap into the feed orientation and emit a portrait @page
// that matches the driver media, plus tell the user to pick "Actual size" (Chromium
// does not push @page size to the driver, so "Fit to page" silently rescales).
//
// Two extra safeties because thermal hardware can't be verified from code:
//  • a "Flip 180°" toggle — rotation direction (90° CW vs CCW) only matters for which
//    way the text reads; the toggle lets the user land it upright either way.
//  • a "Download print-ready PDF" — a PDF MediaBox is honoured as true media far more
//    reliably than CSS @page, so it's the better fallback when browser printing fights
//    the driver.
import { state } from './state'
import { labelMm } from './canvas'
import { toast } from './ui/dom'
import type { LabelSize } from './types'

// Brands whose die-cut rolls are PROVEN to feed short-edge-first (narrow
// LabelWriter-style media where the label's long side runs along the feed).
// Deliberately NOT the whole thermal family: TSC / Zebra / Rollo-class desktop
// printers have wide (4") heads and feed labels wide-side-first, i.e. their
// driver media is LANDSCAPE — rotating their output 90° clipped the label and
// misfed the roll (customer report, TSC, 2026-08-23). Those print as designed.
const SHORT_EDGE_FEED_BRANDS = ['dymo']
const NON_DIECUT_CATEGORIES = ['tape', 'continuous', 'sheet', 'document']

export function isThermalDieCut(size: LabelSize): boolean {
  const brand = (size.brand || '').toLowerCase()
  const cat = (size.category || '').toLowerCase()
  if (NON_DIECUT_CATEGORIES.includes(cat)) return false
  return SHORT_EDGE_FEED_BRANDS.includes(brand)
}

/** Rotate a PNG dataURL by 90 / 180 / 270 degrees clockwise; returns a new dataURL. */
function rotate(dataUrl: string, deg: 90 | 180 | 270): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const c = document.createElement('canvas')
      const swap = deg === 90 || deg === 270
      c.width = swap ? img.height : img.width
      c.height = swap ? img.width : img.height
      const ctx = c.getContext('2d')
      if (!ctx) { reject(new Error('rotate: no 2d context')); return }
      ctx.translate(c.width / 2, c.height / 2)
      ctx.rotate((deg * Math.PI) / 180)
      ctx.drawImage(img, -img.width / 2, -img.height / 2)
      resolve(c.toDataURL('image/png'))
    }
    img.onerror = () => reject(new Error('rotate: image load failed'))
    img.src = dataUrl
  })
}

/**
 * Print a DYMO-style die-cut label: rotate into portrait feed orientation and
 * fire the system print dialog immediately — no visible helper UI (user
 * decision 2026-08-21; the old instruction screen was disliked). The rotation
 * is the actual fix for the "#30252 blank/misaligned" bug and must stay.
 * `png` is the canvas snapshot in the design (display) orientation.
 */
export async function printThermalLabel(png: string, dims = labelMm(), sizeName = state.currentSize.name): Promise<void> {
  const shortMM = Math.min(dims.w, dims.h)
  const longMM = Math.max(dims.w, dims.h)
  const isLandscape = dims.w > dims.h
  const portrait = isLandscape ? await rotate(png, 90) : png

  const win = window.open('', '_blank')
  if (!win) { toast('Allow pop-ups to print'); return }

  const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))
  win.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Print ${esc(sizeName)}</title>` +
    `<style>@page{size:${shortMM}mm ${longMM}mm;margin:0}body{margin:0}` +
    `img{width:${shortMM}mm;height:${longMM}mm;display:block}</style></head>` +
    // print on image load; close the tab once the dialog is dismissed
    `<body><img src="${portrait}" onload="window.print()">` +
    `<script>window.onafterprint=function(){window.close()}<\/script></body></html>`,
  )
  win.document.close()
}
